import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { compact } from 'lodash-es';
import { DropdownMenu } from 'seagull';
import { ActivityLog, Copy, DocumentCheckmark, Edit, EmailSent, LinkIcon, Eye, Download, Trash } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useOverlayContext } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { WithdrawModal } from '~/pages/ProposalsDashboard/WithdrawModal/WithdrawModal';
const deprecatedTestIds = {
    ProposalRowMenuItemsTestIds: {
        ViewProposal: 'proposals-row-view-proposal',
        Edit: 'proposals-row-withdraw-to-edit',
        CopyLink: (isSample)=>isSample ? 'test-proposal-copy-link' : 'proposals-row-copy-link',
        SendReminder: 'proposals-row-send-reminder',
        Duplicate: 'proposals-row-duplicate',
        ActivityLog: 'view-activity-log',
        DownloadPdf: 'proposals-row-download-pdf',
        ApproveOnBehalf: 'proposals-row-approve-on-behalf',
        Cancel: (isSample)=>isSample ? 'test-proposal-cancel' : 'proposals-row-cancel'
    }
};
export const GetProposalRowMenuItemsTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds
    });
export function useProposalRowMenuItems(param) {
    let { proposal, isVendor, expired, proposalActions, approveOnBehalf, onViewProposal, onViewActivityLog, onDownloadPDF, refresh } = param;
    const { t: proposalTexts } = useLocaleKeys('pages.proposalsDashboard');
    const { EDIT_PROPOSAL_IN_PLACE } = useFeatureFlags();
    const overlayContext = useOverlayContext();
    const { ProposalRowMenuItemsTestIds } = useMemo(()=>GetProposalRowMenuItemsTID(), []);
    const { getCancelProposalAction, getCopyProposalLinkAction, getDuplicateProposalAction, getEditProposalAction, getApproveOnBehalfAction, getSendProposalReminderAction } = proposalActions;
    const heapId = proposal.isSample ? 'test-proposal' : 'proposals';
    function renderSendProposalReminderAction() {
        const { intent, disabled, text, onClick } = getSendProposalReminderAction(proposal.id, proposal.isSample);
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_send_reminder'
            },
            intent: intent,
            disabled: disabled,
            onClick: onClick,
            iconStart: /*#__PURE__*/ _jsx(EmailSent, {}),
            testId: ProposalRowMenuItemsTestIds.SendReminder(),
            children: text
        }, "reminder".concat(proposal.id));
    }
    function renderCopyProposalLinkAction() {
        const { intent, text, onClick } = getCopyProposalLinkAction(proposal);
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_copy_link'
            },
            intent: intent,
            onClick: onClick,
            iconStart: /*#__PURE__*/ _jsx(LinkIcon, {}),
            testId: ProposalRowMenuItemsTestIds.CopyLink(proposal.isSample),
            children: text
        }, "copy-link-".concat(proposal.id));
    }
    function renderDuplicateProposalAction() {
        const { intent, disabled, text, onClick } = getDuplicateProposalAction(proposal.id, {
            isSampleProposal: proposal.isSample,
            draftId: proposal.draftId
        });
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_duplicate'
            },
            intent: intent,
            iconStart: /*#__PURE__*/ _jsx(Copy, {}),
            disabled: disabled,
            testId: ProposalRowMenuItemsTestIds.Duplicate(),
            onClick: onClick,
            children: text
        }, "duplicate".concat(proposal.id));
    }
    function renderEditProposalAction() {
        const { text, intent, disabled, onClick } = getEditProposalAction(proposal, 'proposalsDashboard');
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_withdraw_to_edit'
            },
            intent: intent,
            onClick: onClick,
            iconStart: /*#__PURE__*/ _jsx(Edit, {}),
            disabled: disabled,
            testId: ProposalRowMenuItemsTestIds.Edit(),
            children: text
        }, "withdraw-to-edit".concat(proposal.id));
    }
    function renderCancelProposalAction() {
        const { text, intent } = getCancelProposalAction(proposal.id, refresh);
        const withdrawProposal = ()=>{
            overlayContext.showModal({
                biName: 'withdraw_modal',
                closeOnClickOutside: true,
                closeOnEscapeKey: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(WithdrawModal, {
                        openedBy: "proposals-dashboard",
                        relationshipId: proposal.id,
                        draftId: proposal.draftId,
                        onSuccess: refresh,
                        dismissModal: dismissModal
                    });
                }
            });
        };
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_cancel'
            },
            skin: "error",
            intent: intent,
            iconStart: /*#__PURE__*/ _jsx(Trash, {}),
            testId: ProposalRowMenuItemsTestIds.Cancel(proposal.isSample),
            onClick: withdrawProposal,
            children: text
        }, "cancel".concat(proposal.id));
    }
    function renderApproveOnBehalfAction(payload) {
        const { intent, text, onClick } = getApproveOnBehalfAction(_object_spread({
            id: proposal.id
        }, payload), refresh);
        const isDisabled = payload.withdrawnForEditing || proposal.isSample || expired || payload.hasExplicitApprovalToS || payload.hasServiceBundles || payload.hasOptionalServices;
        let tooltipMessage;
        if (payload.withdrawnForEditing) {
            tooltipMessage = proposalTexts.approveOnBehalfWithdrawnTooltip();
        } else if (expired) {
            tooltipMessage = proposalTexts.approveOnBehalfExpiredTooltip();
        } else if (proposal.isSample) {
            tooltipMessage = proposalTexts.approveOnBehalfSampleTooltip();
        } else if (payload.hasExplicitApprovalToS) {
            tooltipMessage = proposalTexts.approveOnBehalfExplicitApprovalTooltip();
        } else if (payload.hasServiceBundles) {
            tooltipMessage = proposalTexts.approveOnBehalfServiceBundlesTooltip();
        } else if (payload.hasOptionalServices) {
            tooltipMessage = proposalTexts.approveOnBehalfOptionalServicesTooltip();
        }
        return /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_approve_on_behalf'
            },
            iconStart: /*#__PURE__*/ _jsx(DocumentCheckmark, {}),
            intent: intent,
            disabled: isDisabled,
            tooltipProps: tooltipMessage != null ? {
                title: tooltipMessage,
                placement: 'top'
            } : undefined,
            testId: ProposalRowMenuItemsTestIds.ApproveOnBehalf(),
            onClick: onClick,
            children: text
        }, "approve-on-behalf-".concat(proposal.id));
    }
    return compact([
        isVendor && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_view_proposal'
            },
            intent: Intent.VendorViewProposal,
            iconStart: /*#__PURE__*/ _jsx(Eye, {}),
            testId: ProposalRowMenuItemsTestIds.ViewProposal(),
            "data-heapid": "".concat(heapId, "-review-btn"),
            onClick: ()=>onViewProposal(proposal),
            children: proposalTexts.mainAction.view()
        }, "view-proposal".concat(proposal.id)),
        isVendor && renderEditProposalAction(),
        isVendor && !expired && renderCopyProposalLinkAction(),
        isVendor && !expired && renderSendProposalReminderAction(),
        isVendor && renderDuplicateProposalAction(),
        isVendor && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'view_activity_log'
            },
            iconStart: /*#__PURE__*/ _jsx(ActivityLog, {}),
            testId: ProposalRowMenuItemsTestIds.ActivityLog(),
            onClick: ()=>onViewActivityLog(proposal),
            children: proposalTexts.viewActivityLog()
        }, "activity".concat(proposal.id)),
        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            bi: {
                name: 'proposals_row_download_pdf'
            },
            intent: Intent.DownloadAgreementPdf,
            iconStart: /*#__PURE__*/ _jsx(Download, {}),
            disabled: proposal.isSample,
            testId: ProposalRowMenuItemsTestIds.DownloadPdf(),
            onClick: ()=>onDownloadPDF(proposal),
            children: proposalTexts.downloadPdf()
        }, "download-pdf-".concat(proposal.id)),
        isVendor && EDIT_PROPOSAL_IN_PLACE && approveOnBehalf != null && renderApproveOnBehalfAction(approveOnBehalf),
        isVendor && renderCancelProposalAction()
    ]);
}
